import React, { useEffect, useState } from "react";
import { QuizScreen } from "../GeneralStyles";
import QuestionPublicLink from "../../component/PublicLink/PublicLink";
import Modal from "../../component/modal/modal";
import styled from "styled-components";

const getItemsPerRow = (mediaLength) => {
  if (mediaLength === 1) return 1;
  if (mediaLength % 3 === 0) return 3;
  if (mediaLength % 4 === 0 || mediaLength >= 5) return 4;
  if (mediaLength % 2 === 0) return 2;
  return 1;
};

const QuizIframeDiv = styled.div`
  width: 100%;
  @media (max-width: 768px) {
    iframe {
      width: 100%;
    }
  }
`;

const StyledImage = styled.img`
  width: ${(props) => props.itemWidth};
  ${(props) => props.itemMaxOne !== "true" && 'max-width: 30vw;'}
  ${(props) => props.itemMaxOne === "true" && 'max-width: 60vw;'}
  margin: 10px;
  cursor: pointer;

  @media (max-width: 768px) {
    /* Middle-wide smartphones and tablets */
    width: calc(50% - 20px); /* 2 items per row */
  }

  @media (max-width: 480px) {
    /* Small smartphones */
    width: 100%; /* 1 item per row */
    margin: 10px 0; /* Remove side margins */
  }
`;

const StyledVideo = styled.video`
  width: ${(props) => props.itemWidth};
  max-width: 300px;
  margin: 10px;
  cursor: pointer;

  @media (max-width: 768px) {
    /* Middle-wide smartphones and tablets */
    width: calc(50% - 20px); /* 2 items per row */
  }

  @media (max-width: 480px) {
    /* Small smartphones */
    width: 100%; /* 1 item per row */
    margin: 10px 0; /* Remove side margins */
  }
`;

export const ModalButton = styled.button`
  border-radius: 50%;
  margin: 0 10px;
  padding: 20px;
  display: block;
  height: 100%;
  background-color: #00000000;
  border: none;
  i {
    width: 20px;
    height: 20px;
    margin: 0 3px;
    background-position: 50%;
    background-repeat: no-repeat;
    padding: 5px;
  }
  i.next-button {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.387 9.503c-1.717 3.6-4.432 6.464-8.055 8.505L.5 16.343c2.888-1.628 5.063-3.728 6.615-6.39l.353-.548a.753.753 0 000-.81L7.13 8.07C5.577 5.4 3.402 3.293.508 1.665L1.34 0c3.63 2.04 6.338 4.905 8.055 8.505.15.308.15.675 0 .99l-.008.008z' fill='%23FFFFFF'/%3E%3C/svg%3E");
    background-size: 12px;
  }
  i.previous-button {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 16.335L8.668 18C5.038 15.96 2.33 13.095.613 9.495a1.145 1.145 0 010-.99C2.33 4.905 5.045 2.04 8.667 0L9.5 1.665C6.605 3.292 4.43 5.4 2.877 8.07l-.337.525a.753.753 0 000 .81l.353.548c1.552 2.662 3.727 4.762 6.615 6.39l-.008-.008z' fill='%23FFFFFF'/%3E%3C/svg%3E");
    background-size: 12px;
  }
`;

const QuizContentMediaType = ({ media }) => {
  const [mediaStyle, setMediaStyle] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const itemsPerRow = getItemsPerRow(media.length);
  const itemWidth = `calc(${100 / itemsPerRow}% - 40px)`; // Adjust width dynamically

  useEffect(() => {
    if (media.length > 1) {
      setMediaStyle(true);
    } else {
      setMediaStyle(false);
    }
  }, [media]);

  const openImageInFullscreen = (index) => {
    setCurrentImageIndex(index);
  };

  const closeFullscreen = () => {
    setCurrentImageIndex(null);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % media.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? media.length - 1 : prevIndex - 1
    );
  };

  return (
    <>
      {media.length === 1 ? (
        media[0].mime.startsWith("image/") ? (
          <StyledImage
            src={media[0].url}
            alt={media[0].alternativeText}
            itemWidth="100%"
            itemMaxOne="true"
            onClick={() => openImageInFullscreen(0)} // Only for images
          />
        ) : (
          <StyledVideo itemWidth="100%" controls>
            <source src={media[0].url} type={media[0].mime} />
            Your browser does not support the video tag.
          </StyledVideo>
        )
      ) : (
        media.map((mediaItem, index) =>
          mediaItem.mime.startsWith("image/") ? (
            <StyledImage
              key={index}
              src={mediaItem.formats.thumbnail.url}
              alt={mediaItem.alternativeText}
              itemWidth={itemWidth}
              itemMaxOne="false"
              onClick={() => openImageInFullscreen(index)} // Only for images
            />
          ) : (
            <StyledVideo
              key={index}
              itemWidth={itemWidth}
              alt={mediaItem.alternativeText}
              controls
              style={{ margin: "10px", cursor: "pointer" }} // Video does not need fullscreen modal
            >
              <source src={mediaItem.url} type={mediaItem.mime} />
              Your browser does not support the video tag.
            </StyledVideo>
          )
        )
      )}

      {currentImageIndex !== null &&
        media[currentImageIndex].mime.startsWith("image/") && (
          <Modal onClose={closeFullscreen}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {media.length !== 1 && (
                <ModalButton onClick={prevImage}>
                  <i className="previous-button"></i>
                </ModalButton>
              )}
              <img
                src={media[currentImageIndex].url}
                alt={media[currentImageIndex].alternativeText}
                style={{ width: "100%", maxWidth: "80vw", maxHeight: "90vh" }}
              />
              {media.length !== 1 && (
                <ModalButton onClick={nextImage}>
                  <i className="next-button"></i>
                </ModalButton>
              )}
            </div>
          </Modal>
        )}
    </>
  );
};

const QuizContentEmbedType = ({ embed }) => {
  return (
    <QuizIframeDiv>
      <div dangerouslySetInnerHTML={{ __html: embed }} />
    </QuizIframeDiv>
  );
};

const QuizContent = ({ question, overViewPage }) => {
  return (
    <QuizScreen
      bgColor={question.theme_color}
      bgVisible={overViewPage}
      id={question.quiz_item_id}
    >
      <QuestionPublicLink
        overViewPage={overViewPage}
        type="question"
        quiz_item_id={question.quiz_item_id}
      />

      {question.type === "media" && (
        <QuizContentMediaType media={question.media} />
      )}
      {question.type === "embed" && (
        <QuizContentEmbedType embed={question.embed} />
      )}
    </QuizScreen>
  );
};

export default QuizContent;
